import React, { useState } from 'react';
import { useError } from './context/ErrorContext';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { HealthProvider } from './context/HealthContext';
import { ErrorProvider } from './context/ErrorContext'; // ADD
import CriticalErrorBanner from './components/Error/CriticalErrorBanner';
import Navbar from './components/Navbar/Navbar';
import LoginModal from './components/Auth/LoginModal';
import SettingsModal from './components/SettingsModal/SettingsModal';
import AboutModal from './components/AboutModal/AboutModal';
import HeroSection from './components/HeroSection/HeroSection';
import TraderDashboard from './components/TraderToolSection/TraderDashboard';
import BacktestDetails from './components/TraderToolSection/BacktestDetails';
import JoinPage from './components/Auth/JoinPage'; 
import PrivacyPolicy from './components/PrivacyPolicy/PrivacyPolicy';
import TermsOfService from './components/TermsOfService/TermsOfService';
import StatusFooter from './components/StatusFooter/StatusFooter';
import './App.css';

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <LanguageProvider>
            <HealthProvider>
              <ErrorProvider> {/* ADD - Inside HealthProvider */}
                <AppContent />
              </ErrorProvider>
            </HealthProvider>
          </LanguageProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

function AppContent() {
  const { user, loading } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const { errors, removeError, clearErrorType } = useError(); // ADD - use error context

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);

  // Get the most recent critical error
  const criticalError = errors.find(e => 
    ['network', 'credits', 'auth', 'backend_down', 'ai_unavailable'].includes(e.type)
  );

  const handleOpenLogin = () => {
    setIsLoginModalOpen(true);
  };
  const handleOpenSettings = () => setIsSettingsModalOpen(true);
  const handleCloseSettings = () => setIsSettingsModalOpen(false);
  const handleOpenAbout = () => setIsAboutModalOpen(true);
  const handleCloseAbout = () => setIsAboutModalOpen(false);

  // Handle error retry
  const handleRetry = () => {
    if (criticalError?.type === 'network') {
      window.location.reload();
    }
    // Add other retry logic as needed
  };

  if (loading) {
    return (
      <div className={`App ${theme}`}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          backgroundColor: 'var(--color-background)',
          color: 'var(--color-text-primary)'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (    
    <div className={`App ${theme}`}>
      <Navbar
        onLoginClick={handleOpenLogin}
        isAuth={!!user}
        pathname={location.pathname}
        onOpenSettings={handleOpenSettings}
        onOpenAbout={handleOpenAbout}
      />
      
      {/* Critical Error Banner - shows at top for serious issues */}
      <CriticalErrorBanner
        error={criticalError}
        onRetry={criticalError?.type === 'network' ? handleRetry : null}
        onDismiss={() => removeError(criticalError?.id)}
      />
      
      <main>
        <Routes>
          <Route path="/" element={user ? <TraderDashboard /> : <HeroSection />} />
          <Route path="/join" element={<JoinPage onOpenLogin={handleOpenLogin} />} />
          <Route path="/backtest/details/:runId" element={user ? <BacktestDetails /> : <Navigate to="/" />} />
          <Route path="/trader" element={<Navigate to="/" replace />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
        </Routes>
      </main>
      <StatusFooter />

      {isLoginModalOpen && (
        <LoginModal 
          isOpen={isLoginModalOpen} 
          onClose={() => {
            setIsLoginModalOpen(false);
            // Clear any auth errors when closing login modal
            clearErrorType('auth');
          }} 
        />
      )}

      <SettingsModal show={isSettingsModalOpen} onClose={handleCloseSettings} />
      <AboutModal show={isAboutModalOpen} onClose={handleCloseAbout} />
    </div>
  );
}

export default App;